
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, Send, MoreVertical, FileText, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { useDropzone } from 'react-dropzone';

export default function MobileInbox() {
    const { API } = useContext(AuthContext);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [textInput, setTextInput] = useState('');

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const response = await axios.get(`${API}/whatsapp/documents`);
            setMessages(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Error al actualizar');
        } finally {
            setLoading(false);
        }
    };

    // Upload Logic (Matches WhatsAppInbox.jsx)
    const onDrop = async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setUploading(true);
        const toastId = toast.loading('Subiendo...');

        try {
            // 1. Upload
            const formData = new FormData();
            formData.append('file', file);
            formData.append('phone', 'APP_UPLOAD');

            const uploadResp = await axios.post(`${API}/whatsapp/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const docId = uploadResp.data.id;

            // 2. Trigger AI
            const reader = new FileReader();
            reader.onload = async () => {
                const base64String = reader.result;
                try {
                    const aiRes = await axios.post(`${API}/ai/process-document`, {
                        file_data: base64String,
                        mime_type: file.type
                    });

                    // SAVE PROPOSAL (Persistence)
                    const proposal = aiRes.data;
                    if (proposal) {
                        await axios.put(`${API}/whatsapp/documents/${docId}/proposal`, {
                            formatted_proposal: proposal
                        });
                    }

                    toast.success('Propuesta generada y guardada', { id: toastId });
                    fetchMessages();
                } catch (e) {
                    console.error(e);
                    toast.error('Error IA', { id: toastId });
                }
            };
            reader.readAsDataURL(file);

        } catch (error) {
            console.error('Upload error:', error);
            const msg = error.response?.data?.detail || error.message;
            toast.error(`Error: ${msg}`, { id: toastId });
        } finally {
            setUploading(false);
        }
    };

    // Text Logic
    const handleSendText = async () => {
        if (!textInput.trim()) return;

        const toastId = toast.loading('Enviando...');
        try {
            // 1. Create message first (we need an ID to attach proposal)
            const blob = new Blob([textInput], { type: 'text/plain' });
            const file = new File([blob], "nota_rapida.txt", { type: "text/plain" });
            const formData = new FormData();
            formData.append('file', file);
            formData.append('phone', 'APP_TEXT');

            const uploadResp = await axios.post(`${API}/whatsapp/upload`, formData);
            const docId = uploadResp.data.id;

            // 2. Call AI
            const aiRes = await axios.post(`${API}/ai/process-document`, {
                text_input: textInput
            });

            // 3. Save Proposal
            if (aiRes.data) {
                await axios.put(`${API}/whatsapp/documents/${docId}/proposal`, {
                    formatted_proposal: aiRes.data
                });
            }

            setTextInput('');
            toast.success('Nota guardada', { id: toastId });
            fetchMessages();

        } catch (error) {
            console.error(error);
            toast.error('Error al enviar', { id: toastId });
        }
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        accept: { 'image/*': [], 'application/pdf': [] }
    });

    return (
        <div className="flex flex-col h-full bg-slate-50 relative" {...getRootProps()}>
            <input {...getInputProps()} />

            {/* Header */}
            <div className="bg-white px-4 py-3 border-b flex justify-between items-center sticky top-0 z-10 shadow-sm safe-top">
                <h1 className="text-lg font-bold text-slate-800">Inbox</h1>
                <div className="flex gap-2">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={fetchMessages}>
                        <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8">
                        <MoreVertical className="h-5 w-5 text-slate-500" />
                    </Button>
                </div>
            </div>

            {/* Messages List */}
            <div className="flex-1 p-3 space-y-3 pb-20 overflow-y-auto">
                {messages.map((msg) => (
                    <Card key={msg.id} className="p-3 shadow-none border border-slate-200">
                        <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                                {/* Icon based on content */}
                                {msg.media_url ? <Camera className="h-3 w-3 text-blue-500" /> : <FileText className="h-3 w-3 text-orange-500" />}
                                <span className="text-[10px] font-bold text-slate-600 uppercase">
                                    {msg.phone === 'APP_UPLOAD' ? 'Móvil' : msg.phone}
                                </span>
                            </div>
                            <span className="text-[10px] text-slate-400">
                                {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                        </div>

                        <p className="text-sm text-slate-800 mb-2 truncate break-words">
                            {msg.text_content || msg.message_body || 'Archivo adjunto'}
                        </p>

                        {msg.file_url && (msg.file_type === 'image' || msg.media_type === 'image') && (
                            <div className="rounded-lg overflow-hidden h-32 w-full bg-slate-100 mb-2 border">
                                <img src={msg.file_url || msg.media_url} alt="adjunto" className="w-full h-full object-cover" />
                            </div>
                        )}

                        <div className="flex justify-between items-center mt-2 border-t pt-2 border-slate-100">
                            <Badge variant={msg.status === 'processed' ? "default" : "secondary"} className="text-[10px] h-5 px-2">
                                {msg.status === 'processed' ? "Procesado" : "Pendiente"}
                            </Badge>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Input Area (Text + Camera) */}
            <div className="fixed bottom-16 left-0 right-0 p-3 bg-white border-t flex gap-2 items-center z-20 pb-safe-bottom">
                <Button
                    size="icon"
                    variant="ghost"
                    className="text-slate-500 shrink-0"
                    onClick={open}
                >
                    <Camera className="h-6 w-6" />
                </Button>

                <Input
                    className="flex-1 bg-slate-100 border-none focus-visible:ring-1"
                    placeholder="Escribe un gasto..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                />

                <Button
                    size="icon"
                    className="shrink-0 bg-primary rounded-full h-10 w-10"
                    onClick={handleSendText}
                    disabled={!textInput.trim()}
                >
                    <Send className="h-5 w-5" />
                </Button>
            </div>

            {uploading && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <Loader2 className="animate-spin h-8 w-8 text-primary mb-2" />
                        <p className="text-sm font-medium">Procesando...</p>
                    </div>
                </div>
            )}

        </div>
    );
}

